import { isNilOrEmpty } from './redux';
import moment from 'moment';
import { compose, defaultTo, find, filter, contains, path, reduce, propEq, prop } from 'ramda';
import magic from './magic';

export function debounce(func, wait, immediate) {
  let timeout;
  return function () {
    const context = this;
    const args = arguments;
    const later = function () {
      timeout = null;
      if (!immediate) func.apply(context, args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func.apply(context, args);
  };
}

export function createMarkup($markup_text) {
  return { __html: $markup_text };
}

export function toCurrency(number, symbol = '$') {
  if (typeof number != 'number') {
    number = Number(number);
  }

  return symbol + number.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
}

export function addPrices(accumulator, lineItem) {
  const paid = !isNilOrEmpty(lineItem.transactionId);

  const price = Number(lineItem.price) || 0;
  const finalPrice = paid ? 0 : Number(lineItem.finalPrice) || 0;
  return {
    total: accumulator.total + price,
    afterDiscount: accumulator.afterDiscount + finalPrice,
  };
}

export function getFileWithKey(files, key) {
  return compose(find(propEq('type', key)), defaultTo([]))(files);
}

export function getDiscountFromLineItems(lineItems) {
  return compose(
    defaultTo({}),
    prop('discount'),
    defaultTo({}),
    find((item) => item.discountId !== null),
  )(lineItems);
}

export const calculatePercent = (current, total) => Math.round((current / total) * 100);

export const snakeToTitle = (input) => input.replace(/(\_\w)/g, (match) => ` ${match[1]}`);

export const validStripeId = (id) => id.indexOf('ch_') === 0;

const getAddressLength = (address) => defaultTo(0, path(['address_components'], address));
const getByType = (components, type) =>
  defaultTo(
    {},
    find((component) => contains(type, component.types), components),
  );
export const getAddressComponents = (addresses) => {
  const bestResult = defaultTo(
    {},
    reduce((a, b) => (getAddressLength(a) > getAddressLength(b) ? a : b), {}, addresses),
  );
  const addressParts = defaultTo({}, bestResult.address_components);
  const streetNumber = getByType(addressParts, 'street_number').long_name;
  const street = getByType(addressParts, 'route').long_name;
  const sub2 = getByType(addressParts, 'sublocality_level_2').long_name;
  const sub3 = getByType(addressParts, 'sublocality_level_3').long_name;
  const sub4 = getByType(addressParts, 'sublocality_level_4').long_name;
  const admin2 = getByType(addressParts, 'administrative_area_level_2').long_name;
  const admin3 = getByType(addressParts, 'administrative_area_level_3').long_name;
  const admin4 = getByType(addressParts, 'administrative_area_level_4').long_name;
  const neighborhood = getByType(addressParts, 'neighborhood').long_name;
  const city = getByType(addressParts, 'locality').long_name;
  const state = getByType(addressParts, 'administrative_area_level_1').short_name;
  const zip = getByType(addressParts, 'postal_code').long_name;
  const country = getByType(addressParts, 'country').long_name;

  return {
    lat: path(['geometry', 'location', 'lat'], bestResult),
    lng: path(['geometry', 'location', 'lng'], bestResult),
    addressLine1: defaultTo('', filter((a) => a, [admin4, admin3, sub4, sub3, sub2, streetNumber, street]).join(' ')),
    city: defaultTo('', city || neighborhood || admin2),
    state: defaultTo('', state),
    zip: defaultTo('', zip),
    country: defaultTo('', country),
  };
};

export const getRegistrationMonthsExpired = (registration) => {
  const monthsExpired =
    registration &&
    registration.deadline &&
    isNilOrEmpty(registration.graduatedOn) &&
    moment().diff(moment(registration.deadline), 'months', true);

  return monthsExpired;
};

export const getRegistrationExpirationInfo = (registration, extensions) => {
  if (!registration) return;

  const monthsExpired = getRegistrationMonthsExpired(registration);
  const isExpired = monthsExpired !== null && monthsExpired !== false && monthsExpired >= 0;
  const extension = find((e) => e.category.id == path(['id'], registration), extensions);

  // Set expiration properties on registration
  if (isExpired && monthsExpired < 6 && !extension) {
    registration.expirationStatus = magic.expiredWithoutExtension;
  } else if (isExpired) {
    registration.expirationStatus = magic.expired;
  } else {
    registration.expirationStatus = magic.notExpired;
  }
  registration.extension = extension;
};

export const getCertificationExtension = (certification, extensions) => {
  const extension = find((e) => e.category.id == path(['id'], certification), extensions);
  certification.extension = extension;
};
